import { BaseError } from '@/util/base-error'

export abstract class CreditcardRegisterError extends BaseError {
  /**
   * デフォルトのメッセージ.
   * エラーごとに異なるメッセージを提示したい場合は、それぞれでオーバーライドすること.
   */
  get label(): string {
    return [
      'クレジットカードの登録中にエラーが発生しました。時間をおいて、再度お試しください。',
      '※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。',
    ].join('<br/>')
  }
}

/**
 * クレジットカード登録スクリプトの取得失敗
 */
export class LoadingCreditcardSciptError extends CreditcardRegisterError {}
/**
 * クレジットカード認証の失敗
 */
export class CreditcardAuthenticationError extends CreditcardRegisterError {
  get label(): string {
    return 'クレジットカード情報を認証できませんでした。<br/>入力内容に誤りがないかご確認お願いします。'
  }
}
/**
 * 不正なクレジットカード番号による登録エラー
 */
export class InvalidCreditcardNumberError extends CreditcardRegisterError {
  get label(): string {
    return '無効なカード番号です。お手元のカード番号をご確認の上、正しい番号を入力してください。'
  }
}
/**
 * 不明なクレジットカード登録エラー
 */
export class UnknownCreditcardRegisterError extends CreditcardRegisterError {
  get label(): string {
    return 'クレジットカードを登録できませんでした。<br/>入力内容に誤りがないかご確認お願いします。'
  }
}

export function stringifyErrorForEndUser(e: CreditcardRegisterError | Error): string {
  return e instanceof CreditcardRegisterError
    ? e.label
    : 'エラーが発生しました。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
}

export class DefaultThreeDSecureRegistrationError extends CreditcardRegisterError {
  get label(): string {
    return buildThreeDSecureRegistrationErrorMessage('')
  }
}

export class ThreeDSecureA081010601Error extends CreditcardRegisterError {
  get label(): string {
    return buildThreeDSecureRegistrationErrorMessage('購入者名(漢字)の設定がありません。')
  }
}

export class ThreeDSecureA081010671Error extends CreditcardRegisterError {
  get label(): string {
    return buildThreeDSecureRegistrationErrorMessage('購入者名(漢字)にサポート対象外の文字が含まれております。 購入者名(漢字)をご確認ください。 また、購入者名(漢字)に半角カナがご利用頂けません。')
  }
}

export class ThreeDSecureA081010871Error extends CreditcardRegisterError {
  get label(): string {
    return buildThreeDSecureRegistrationErrorMessage('購入者E-Mailが誤っております。 購入者E-Mailをご確認ください。')
  }
}

export class ThreeDSecureA081010872Error extends CreditcardRegisterError {
  get label(): string {
    return buildThreeDSecureRegistrationErrorMessage('購入者E-mailのアカウント部分（@(アットマーク)より前）もしくはドメイン部分（@(アットマーク)より後）が入力されておりません。 購入者E-mailをご確認ください。')
  }
}

export class ThreeDSecureA082000008Error extends CreditcardRegisterError {
  get label(): string {
    return buildThreeDSecureRegistrationErrorMessage('クレジットカード情報に誤りがあります。カード番号や有効期限等をご確認ください。')
  }
}

export class ThreeDSecureA082000010Error extends CreditcardRegisterError {
  constructor(private readonly creditErrorCode: string) {
    super()
  }

  get label(): string {
    return buildThreeDSecureRegistrationErrorMessage([
      'クレジットカード情報に誤りがあります。名義人や有効期限等をご確認ください。',
      `カード会社にお問い合わせする際は 与信結果エラーコード ${this.creditErrorCode} をお伝えください。`,
    ].join('<br>'))
  }
}

export class ThreeDSecureA082000011Error extends CreditcardRegisterError {
  get label(): string {
    return buildThreeDSecureRegistrationErrorMessage('クレジットカードは既に登録済みの可能性があります。')
  }
}

function buildThreeDSecureRegistrationErrorMessage(detail: string): string {
  return [
    'クレジットカードを登録できませんでした。',
    detail,
    '※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。',
  ].join('<br/>')
}

export function errorOnThreeDSecureRegistration(
  { errorCode, creditErrorCode }: {
    errorCode: string,
    creditErrorCode?: string
  }
): CreditcardRegisterError {
  switch (errorCode) {
    case 'A081010601':
      return new ThreeDSecureA081010601Error()
    case 'A081010671':
      return new ThreeDSecureA081010671Error()
    case 'A081010871':
      return new ThreeDSecureA081010871Error()
    case 'A081010872':
      return new ThreeDSecureA081010872Error()
    case 'A082000008':
      return new ThreeDSecureA082000008Error()
    case 'A082000010':
      return new ThreeDSecureA082000010Error(creditErrorCode || '')
    case 'A082000011':
      return new ThreeDSecureA082000011Error()
    default:
      return new DefaultThreeDSecureRegistrationError()
  }
}

export class ThreeDAuthHtmlParseError extends CreditcardRegisterError {
  get label(): string {
    return '3Dセキュア認証画面を表示できませんでした。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
  }
}

export class ThreeDAuthHtmlTransitionError extends CreditcardRegisterError {
  get label(): string {
    return '3Dセキュア認証画面を表示できませんでした。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
  }
}
